import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Chip,
} from "@mui/material";
import { accessRequestAPI } from "../../services/api";
import { useSnackbar } from "../../contexts/SnackbarContext";

const AccessRequestManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewDialog, setReviewDialog] = useState({
    open: false,
    request: null,
  });
  const [reviewNotes, setReviewNotes] = useState("");
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const fetchRequests = async () => {
    try {
      const response = await accessRequestAPI.getAll();
      setRequests(response.data);
    } catch (error) {
      console.error("Error fetching access requests:", error);
      showSnackbar(t("admin.accessRequests.messages.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleReviewOpen = (request) => {
    setReviewDialog({ open: true, request });
    setReviewNotes("");
  };

  const handleReviewClose = () => {
    setReviewDialog({ open: false, request: null });
    setReviewNotes("");
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const request = reviewDialog.request;
      console.log("Approving request:", {
        id: request.id,
        user_id: request.user_id,
        book_id: request.book_id,
        request_type: request.request_type,
      });

      await accessRequestAPI.approve(request.id, reviewNotes);
      showSnackbar(
        t("admin.accessRequests.messages.approveSuccess"),
        "success"
      );
      handleReviewClose();
      fetchRequests();
    } catch (error) {
      console.error("Error approving request:", error);
      showSnackbar(t("admin.accessRequests.messages.updateFailed"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeny = async () => {
    setActionLoading(true);
    try {
      await accessRequestAPI.deny(reviewDialog.request.id, reviewNotes);
      showSnackbar(t("admin.accessRequests.messages.updateSuccess"), "success");
      handleReviewClose();
      fetchRequests();
    } catch (error) {
      console.error("Error denying request:", error);
      showSnackbar(t("admin.accessRequests.messages.updateFailed"), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "denied":
        return "error";
      default:
        return "warning";
    }
  };

  if (loading) {
    return <Typography>{t("common.loading")}</Typography>;
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t("admin.bookManagement.tabs.accessRequests")}
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.accessRequests.table.user")}</TableCell>
              <TableCell>{t("admin.accessRequests.table.book")}</TableCell>
              <TableCell>{t("admin.accessRequests.table.type")}</TableCell>
              <TableCell>{t("admin.accessRequests.table.status")}</TableCell>
              <TableCell>
                {t("admin.accessRequests.table.requestedAt")}
              </TableCell>
              <TableCell>{t("admin.accessRequests.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.requester?.username}</TableCell>
                <TableCell>{request.Book?.title}</TableCell>
                <TableCell>
                  {t(`admin.accessRequests.dialog.${request.request_type}`)}
                  {request.request_type === "section" &&
                    ` (${request.section_start}-${request.section_end})`}
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(`admin.accessRequests.status.${request.status}`)}
                    color={getStatusChipColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {request.createdAt
                    ? new Date(request.createdAt).toLocaleDateString(
                        undefined,
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        }
                      )
                    : t("common.notAvailable")}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {request.status === "pending" && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleReviewOpen(request)}
                      >
                        {t("admin.accessRequests.actions.review")}
                      </Button>
                    )}
                    {request.status !== "pending" && (
                      <Button
                        variant="outlined"
                        color="info"
                        size="small"
                        onClick={() => handleReviewOpen(request)}
                      >
                        {t("admin.accessRequests.actions.view")}
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={reviewDialog.open}
        onClose={handleReviewClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {reviewDialog.request?.status === "pending"
            ? t("admin.accessRequests.dialog.reviewTitle")
            : t("admin.accessRequests.dialog.detailsTitle")}
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography>
              <strong>{t("admin.accessRequests.dialog.user")}:</strong>{" "}
              {reviewDialog.request?.requester?.username}
            </Typography>
            <Typography>
              <strong>{t("admin.accessRequests.dialog.book")}:</strong>{" "}
              {reviewDialog.request?.Book?.title}
            </Typography>
            <Typography>
              <strong>{t("admin.accessRequests.dialog.requestType")}:</strong>{" "}
              {t(
                `admin.accessRequests.dialog.${reviewDialog.request?.request_type}`
              )}
            </Typography>
            {reviewDialog.request?.request_type === "section" && (
              <Typography>
                <strong>{t("admin.accessRequests.dialog.section")}:</strong>{" "}
                {reviewDialog.request?.section_start} -{" "}
                {reviewDialog.request?.section_end}
              </Typography>
            )}
            <Typography>
              <strong>{t("admin.accessRequests.dialog.reason")}:</strong>{" "}
              {reviewDialog.request?.reason ||
                t("admin.accessRequests.dialog.noReason")}
            </Typography>
            {reviewDialog.request?.status !== "pending" && (
              <>
                <Typography>
                  <strong>{t("admin.accessRequests.dialog.status")}:</strong>{" "}
                  <Chip
                    label={t(
                      `admin.accessRequests.status.${reviewDialog.request?.status}`
                    )}
                    color={getStatusChipColor(reviewDialog.request?.status)}
                    size="small"
                  />
                </Typography>
                {reviewDialog.request?.review_notes && (
                  <Typography>
                    <strong>
                      {t("admin.accessRequests.dialog.notesLabel")}:
                    </strong>{" "}
                    {reviewDialog.request?.review_notes}
                  </Typography>
                )}
                {reviewDialog.request?.reviewed_at && (
                  <Typography>
                    <strong>
                      {t("admin.accessRequests.dialog.reviewed")}:
                    </strong>{" "}
                    {new Date(
                      reviewDialog.request?.reviewed_at
                    ).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                )}
              </>
            )}
          </Box>
          {reviewDialog.request?.status === "pending" && (
            <TextField
              autoFocus
              margin="dense"
              label={t("admin.accessRequests.dialog.notesLabel")}
              fullWidth
              multiline
              rows={4}
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReviewClose}>
            {reviewDialog.request?.status === "pending"
              ? t("common.cancel")
              : t("common.close")}
          </Button>
          {reviewDialog.request?.status === "pending" && (
            <>
              <Button
                onClick={handleDeny}
                color="error"
                disabled={actionLoading}
              >
                {actionLoading
                  ? t("admin.accessRequests.actions.processing")
                  : t("admin.accessRequests.actions.deny")}
              </Button>
              <Button
                onClick={handleApprove}
                color="primary"
                variant="contained"
                disabled={actionLoading}
              >
                {actionLoading
                  ? t("admin.accessRequests.actions.processing")
                  : t("admin.accessRequests.actions.approve")}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccessRequestManager;
